"use client";

import React, { useEffect, useRef, useState } from "react";

import { CloseIcon } from "@/components/icons/CloseIcon";
import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/utils/general";
import { segmentEvent } from "@/utils/segmentfunctions";

export type HeroVideoProps = {
  id?: string;
  headline: string;
  copy?: string;
  buttonLabel: string;
  videos: {
    name: string;
    src: string;
    poster: string;
    srcMobile?: string;
    posterMobile?: string;
  }[];
  videosModal: {
    mobile: { name: string; poster: string; src: string };
    desktop: { name: string; poster: string; src: string };
  };
};

const PlayIconDefault = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden
  >
    <path
      d="M18.5 11.134C19.1667 11.5189 19.1667 12.4811 18.5 12.866L9.5 18.0622C8.83333 18.4471 8 17.966 8 17.1962L8 6.80385C8 6.03405 8.83333 5.55292 9.5 5.93782L18.5 11.134Z"
      fill="white"
    />
  </svg>
);

/** Hover: solid white tile + dark play — same asset as headless PlayIconAlt.svg */
const PlayIconHover = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden
  >
    <rect x="0" y="0" width="24" height="24" fill="white" />
    <path
      d="M18.5 11.134C19.1667 11.5189 19.1667 12.4811 18.5 12.866L9.5 18.0622C8.83333 18.4471 8 17.966 8 17.1962L8 6.80385C8 6.03405 8.83333 5.55292 9.5 5.93782L18.5 11.134Z"
      fill="#041c1b"
    />
  </svg>
);

function VideoModal({
  show,
  onClose,
  src,
  poster,
  name,
}: {
  show: boolean;
  onClose: () => void;
  src: string;
  poster: string;
  name: string;
}) {
  const modalRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isFirstPlayEvent = useRef(true);

  useEffect(() => {
    if (!show) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show, onClose]);

  useEffect(() => {
    if (!show) return;
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handlePlay = () => {
      if (isFirstPlayEvent.current && videoElement.autoplay) {
        isFirstPlayEvent.current = false;
        return;
      }
      segmentEvent("Video Played", {
        module_state: "Play",
        video_name: name,
      });
    };

    const handlePause = () => {
      segmentEvent("Video Played", {
        module_state: "Paused",
        video_name: name,
      });
    };

    videoElement.addEventListener("play", handlePlay);
    videoElement.addEventListener("pause", handlePause);
    return () => {
      videoElement.removeEventListener("play", handlePlay);
      videoElement.removeEventListener("pause", handlePause);
      isFirstPlayEvent.current = true;
    };
  }, [show, name, src]);

  if (!show) return null;

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-[1000] backdrop-blur-[6px]"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-master-primary-default opacity-80" onClick={onClose} />
      <div className="relative flex flex-col h-full items-center justify-center px-6 pointer-events-none">
        <div className="pointer-events-auto flex flex-col gap-y-4 items-center max-w-[960px] w-full">
          <video
            className="modal-video w-full max-h-[70vh]"
            ref={videoRef}
            key={src}
            src={src}
            poster={poster}
            controls
            autoPlay
            playsInline
          >
            Your browser does not support the video tag.
          </video>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="relative inline-flex items-center gap-2 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-px after:bg-master-base-default group"
          >
            <CloseIcon className="transition-transform duration-200 group-hover:-rotate-90" />
            <TextElementNova text="Close" className="CTA-20-Dia-Normal text-nova-base-white" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function HeroVideo(props: HeroVideoProps) {
  const { id, headline, copy, buttonLabel, videos, videosModal } = props;
  const [modalVisible, setModalVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const bgVideoRef = useRef<HTMLVideoElement>(null);
  const isMobile = useIsMobile(768);

  const videoModal = isMobile ? videosModal.mobile : videosModal.desktop;
  const bgVideo = videos[0]
    ? {
        ...videos[0],
        src: isMobile ? videos[0].srcMobile || videos[0].src : videos[0].src,
        poster: isMobile ? videos[0].posterMobile || videos[0].poster : videos[0].poster,
      }
    : null;

  useEffect(() => {
    setIsPaused(modalVisible);
  }, [modalVisible]);

  useEffect(() => {
    const el = bgVideoRef.current;
    if (!el) return;
    if (isPaused) {
      el.pause();
    } else {
      void el.play().catch((): undefined => undefined);
    }
  }, [isPaused, videoModal]);

  const showModal = () => {
    setModalVisible(true);
    segmentEvent("Modal Viewed", { name: buttonLabel });
  };

  return (
    <section
      id={id}
      className={cn(
        "flex items-center relative overflow-hidden justify-center",
        "md:min-h-[432px] md:h-auto",
        "lg:mt-0 lg:min-h-[900px]"
      )}
    >
      <div
        className={cn(
          "relative bg-transparent z-[9] min-h-[150vw] p-0 flex mx-auto items-center",
          "md:h-auto md:min-h-[200px] md:max-w-[560px]",
          "lg:max-w-[800px] lg:pt-[100px]"
        )}
      >
        <div className="text-white max-w-[334px] pt-[20%] md:max-w-none md:flex md:flex-col md:p-0 md:m-auto md:items-start">
          {copy && (
            <TextElementNova
              text={copy}
              element="h5"
              className="Caption-10-Dia-Uppercase text-nova-base-white text-center w-full pb-1"
            />
          )}
          {headline && (
            <div className="break-words text-center whitespace-pre-wrap md:text-left">
              <TextElementNova
                text={headline}
                element="h1"
                classes={{
                  default: "Title-30-Super-Normal",
                  768: "Title-30-Super-Normal",
                  1280: "Title-30-Super-Normal",
                }}
                className="text-nova-base-white text-center [text-shadow:_1px_1px_2px_rgba(0,0,0,0.6)]"
              />
            </div>
          )}
          <div className="flex justify-center mx-auto w-auto min-w-[260px] max-w-full pt-10 md:pt-6 lg:pt-10 md:min-w-[300px] lg:min-w-[420px]">
            <ButtonNova
              label=""
              level="Secondary"
              size="Default"
              onImageBackground
              className={cn(
                "group hover:text-nova-base-black w-full",
                "!bg-white/10 !border !border-white/40 backdrop-blur-[40px]",
                "hover:!bg-white/60 hover:backdrop-blur-none",
                "max-lg:!px-2 max-lg:!py-[7px]"
              )}
              onClick={showModal}
            >
              <div className="flex items-center gap-4 whitespace-nowrap">
                {/* Same 32/64 box for both states so hover doesn't shrink the button (Gatsby svg sizing) */}
                <div className="relative w-8 h-8 lg:w-16 lg:h-16 shrink-0">
                  <div className="absolute inset-0 group-hover:invisible rounded bg-white/20 flex items-center justify-center overflow-hidden">
                    <PlayIconDefault className="w-full h-full" />
                  </div>
                  <div className="absolute inset-0 invisible group-hover:visible overflow-hidden rounded-sm">
                    <PlayIconHover className="w-full h-full" />
                  </div>
                </div>
                <TextElementNova
                  text={buttonLabel}
                  className="text-nova-base-white text-center Subtitle-10-Dia-Normal group-hover:text-nova-base-black max-[389px]:!text-base"
                />
              </div>
            </ButtonNova>
          </div>
        </div>
      </div>

      <div className="absolute inset-0 z-0 w-full h-full bg-master-base-black">
        {bgVideo && (
          <video
            ref={bgVideoRef}
            className="object-cover inset-0 w-screen h-full md:min-h-[432px] lg:min-h-[900px]"
            playsInline
            autoPlay
            muted
            loop
            src={bgVideo.src}
            poster={bgVideo.poster}
          >
            Your browser does not support the video tag.
          </video>
        )}
      </div>

      <VideoModal
        show={modalVisible && !!videoModal}
        onClose={() => setModalVisible(false)}
        src={videoModal.src}
        poster={videoModal.poster}
        name={videoModal.name}
      />
    </section>
  );
}
